/**
 * Router Resolver — Sequence-Based API Endpoint Resolution
 *
 * Single responsibility: translate a (tsxSeq, ordinal, prefix) tuple into
 * a backend API path string.
 *
 * ARCHITECTURE:
 * - tsxbound routers always follow: /api/{prefix}/{seq}/{ordinal}
 * - Legacy infrastructure routers have custom paths in router-manifest.json
 * - Manifest lookup is authoritative; calculated path is the fallback.
 *
 * This file owns ALL routing resolution logic.
 * apiService.ts owns ALL HTTP transport logic.
 * These two concerns must not be mixed.
 *
 * USAGE:
 *   import { resolveRouterEndpoint } from './routerResolver';
 *   const endpoint = resolveRouterEndpoint(10375, 1); // "/api/tsx/10375/1"
 *   apiRequest(endpoint, 'POST', { manage: 5 });
 */

import routerManifestRaw from '../manifests/router-manifest.json';

// ─────────────────────────────────────────────────────────────
// Manifest Map (loaded once at app startup via loadRouterManifest)
// ─────────────────────────────────────────────────────────────

const routerManifestMap = new Map<string, string>();

/**
 * Load router manifest into memory at app startup.
 * Called once from App.tsx useEffect.
 */
export function loadRouterManifest() {
    Object.entries(routerManifestRaw).forEach(([_key, value]: [string, any]) => {
        const filename = value.filepath.split('/').pop(); // e.g. tsx_10031_1.py
        if (filename && value.prefix) {
            routerManifestMap.set(filename, value.prefix);
        }
    });
    console.log(`[routerResolver] Loaded ${routerManifestMap.size} routers from manifest`);
}

// ─────────────────────────────────────────────────────────────
// Endpoint Resolution
// ─────────────────────────────────────────────────────────────

/**
 * Resolve API endpoint from TSX sequence + router ordinal.
 *
 * For tsxbound routers the path is deterministic:
 *   /api/{prefix}/{seq}/{ordinal}
 *
 * The manifest lookup supports legacy infrastructure routes that use
 * non-standard path names. For tsxbound routes the calculated path and
 * the manifest value are identical, so the fallback is always safe.
 *
 * @param tsxSeq  - Component sequence number (@tg-seq)
 * @param ordinal - Router ordinal (1, 2, 3...)
 * @param prefix  - Router prefix: 'tsx' (standard) | 'sec' (secure VPN)
 * @returns       - Absolute API path, e.g. "/api/tsx/10375/1"
 *
 * @example
 * resolveRouterEndpoint(10375, 1)        // "/api/tsx/10375/1"
 * resolveRouterEndpoint(10833, 2)        // "/api/tsx/10833/2"
 * resolveRouterEndpoint(10833, 1, 'sec') // "/api/sec/10833/1"
 */
export function resolveRouterEndpoint(
    tsxSeq: number,
    ordinal: number,
    prefix: string = 'tsx',
): string {
    // Manifest key format: "tsx/10375/1"
    const lookupKey = `${prefix}/${tsxSeq}/${ordinal}`;
    const manifestEntry = routerManifestRaw[lookupKey as keyof typeof routerManifestRaw];

    if (manifestEntry && (manifestEntry as any).prefix) {
        return (manifestEntry as any).prefix;
    }

    // Calculated fallback — always correct for tsxbound routers
    console.warn(`[routerResolver] "${lookupKey}" not in manifest — using calculated path`);
    return `/api/${prefix}/${tsxSeq}/${ordinal}`;
}

/**
 * Backward-compat alias used by older components.
 * Prefer resolveRouterEndpoint for new code.
 */
export function resolveRouterBySeq(seq: number, ordinal: number = 1): string {
    return resolveRouterEndpoint(seq, ordinal);
}
